import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAuthStore,
  type AuthSession,
  type AuthenticatedUser,
  type SessionTokens,
} from '../store/auth-store';
import { refreshSession } from '../api/auth';
import { notificationsClient } from '../realtime/notifications-client';
import { clearCheckoutUpgradeIntent } from '../utils/checkout-intent';

const STORAGE_KEY = 'socialforge.session';

type AuthContextValue = {
  tokens: SessionTokens | null;
  user: AuthenticatedUser | null;
  isHydrated: boolean;
  isSessionRestoredFromStorage: boolean;
  setSession: (session: AuthSession | null) => void;
  updateTokens: (tokens: SessionTokens | null) => void;
  setUser: (user: AuthenticatedUser | null) => void;
  onboardingTarget: 'platforms' | null;
  setOnboardingTarget: (target: 'platforms' | null) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const AuthProvider = ({ children }: PropsWithChildren) => {
  const tokens = useAuthStore((state) => state.tokens);
  const user = useAuthStore((state) => state.user);
  const sessionId = useAuthStore((state) => state.sessionId);
  const setSession = useAuthStore((state) => state.setSession);
  const updateTokens = useAuthStore((state) => state.updateTokens);
  const setUser = useAuthStore((state) => state.setUser);
  const [hydrated, setHydrated] = useState(false);
  const [onboardingTarget, setOnboardingTarget] = useState<'platforms' | null>(null);
  const [isSessionRestoredFromStorage, setIsSessionRestoredFromStorage] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    try {
      const persisted = window.localStorage.getItem(STORAGE_KEY);
      if (persisted) {
        const parsed = JSON.parse(persisted) as AuthSession | null;
        if (parsed?.tokens) {
          setSession({
            tokens: parsed.tokens,
            user: parsed.user ?? null,
            sessionId: parsed.sessionId ?? null,
            onboardingTarget: parsed.onboardingTarget ?? null,
          });
          setOnboardingTarget(parsed.onboardingTarget ?? null);
          setIsSessionRestoredFromStorage(true);
        }
      }
    } catch (error) {
      console.warn('Failed to hydrate session tokens', error);
    }
    setHydrated(true);
  }, [setSession]);

  useEffect(() => {
    try {
      if (!hydrated) {
        return;
      }
      if (tokens) {
        const snapshot: AuthSession = { tokens, user: user ?? null, sessionId, onboardingTarget };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to persist session tokens', error);
    }
  }, [tokens, user, sessionId, hydrated, onboardingTarget]);

  const value = useMemo<AuthContextValue>(
    () => ({
      tokens,
      user,
      isHydrated: hydrated,
      isSessionRestoredFromStorage,
      setSession: (session) => {
        if (session?.user?.id !== user?.id) {
          queryClient.removeQueries({ queryKey: ['current-user'] });
          queryClient.removeQueries({ queryKey: ['platforms', 'accounts'] });
          clearCheckoutUpgradeIntent();
        }
        setOnboardingTarget(session?.onboardingTarget ?? null);
        setIsSessionRestoredFromStorage(false);
        setSession(session);
      },
      updateTokens,
      setUser,
      onboardingTarget,
      setOnboardingTarget,
    }),
    [tokens, user, hydrated, isSessionRestoredFromStorage, setSession, updateTokens, setUser, onboardingTarget, queryClient],
  );

  useEffect(() => {
    if (!tokens?.refreshToken || !tokens?.expiresAt) {
      return;
    }

    const refreshDelay = Math.max(tokens.expiresAt - Date.now() - 60_000, 10_000);

    const timer = window.setTimeout(async () => {
      try {
        const refreshed = await refreshSession({ refreshToken: tokens.refreshToken! });
        updateTokens({
          accessToken: refreshed.accessToken,
          refreshToken: refreshed.refreshToken ?? tokens.refreshToken,
          expiresAt: refreshed.expiresIn ? Date.now() + refreshed.expiresIn * 1000 : tokens.expiresAt,
          idToken: refreshed.token ?? tokens.idToken,
        });
      } catch (error) {
        console.warn('Session refresh failed', error);
        setSession(null);
      }
    }, refreshDelay);

    return () => window.clearTimeout(timer);
  }, [tokens, updateTokens, setSession]);

  useEffect(() => {
    const unsubscribe = notificationsClient.on('message', (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (!payload || typeof payload !== 'object') {
          return;
        }

        if (payload.type === 'credits.updated') {
          const data = payload.data ?? {};
          const balances = data.balances ?? {};
          const monthly = typeof balances.monthly === 'number' ? balances.monthly : undefined;
          const purchased = typeof balances.purchased === 'number' ? balances.purchased : undefined;
          const total =
            typeof balances.total === 'number'
              ? balances.total
              : monthly !== undefined && purchased !== undefined
                ? monthly + purchased
                : undefined;

          queryClient.setQueryData(['credits', 'balance'], (previous: unknown) => {
            if (!previous || typeof previous !== 'object') {
              return previous;
            }

            const next = { ...(previous as Record<string, unknown>) };
            if (monthly !== undefined) {
              next.monthlyBalance = monthly;
            }
            if (purchased !== undefined) {
              next.purchasedBalance = purchased;
            }
            if (total !== undefined) {
              next.totalBalance = total;
            }
            return next;
          });

          queryClient.invalidateQueries({ queryKey: ['billing', 'usage'], exact: true });
          queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'], exact: true });
        }

        if (payload.type === 'post.updated') {
          queryClient.invalidateQueries({ queryKey: ['posts', 'scheduled', 'summary'] });
          queryClient.invalidateQueries({ queryKey: ['posts'] });
        }
      } catch (error) {
        console.debug('[notifications] ignored non-json message', { error });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  useEffect(() => {
    if (tokens?.accessToken) {
      notificationsClient.connect().catch((error) => {
        console.error('Failed to start notifications client', error);
      });
      return () => {
        notificationsClient.disconnect();
      };
    }

    notificationsClient.disconnect();
    return undefined;
  }, [tokens?.accessToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
